import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Ruler, Plus, Trash, ChartLine } from '@phosphor-icons/react';
import { API_URL } from '../config/api';

/**
 * CalibrationCard — admin-only tool to log Moasure-measured lawn sqft
 * alongside the AI Scan estimate so we can track and correct AI bias.
 */
export const CalibrationCard = () => {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ address: '', moasure_sqft: '', ai_sqft: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/calibrations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/calibrations/stats`, { withCredentials: true }),
      ]);
      setRows(listRes.data.calibrations || []);
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to load calibrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.address.trim() || !form.moasure_sqft) {
      toast.error('Address and Moasure sqft are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        address: form.address.trim(),
        moasure_sqft: parseFloat(form.moasure_sqft),
        notes: form.notes.trim(),
      };
      if (form.ai_sqft) payload.ai_sqft = parseFloat(form.ai_sqft);

      await axios.post(`${API_URL}/api/admin/calibrations`, payload, { withCredentials: true });
      toast.success('Calibration logged');
      setForm({ address: '', moasure_sqft: '', ai_sqft: '', notes: '' });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this calibration sample?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/calibrations/${id}`, { withCredentials: true });
      toast.success('Deleted');
      await load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const fmtErr = (e) => {
    if (e === null || e === undefined) return <span className="text-muted-foreground">—</span>;
    const sign = e > 0 ? '+' : '';
    const color = Math.abs(e) > 15 ? 'text-red-600 dark:text-red-400' : Math.abs(e) > 7 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400';
    return <span className={`font-mono font-semibold ${color}`}>{sign}{e}%</span>;
  };

  return (
    <Card data-testid="calibration-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Ruler weight="duotone" size={22} className="text-primary" />
          <div>
            <CardTitle>AI Scan Calibration</CardTitle>
            <CardDescription>
              Log Moasure-measured lawn sqft vs AI Scan estimates. Helps tune the AI bias over time.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="calibration-stats">
            <StatBox label="Samples" value={stats.samples ?? 0} />
            <StatBox label="Mean bias" value={stats.mean_bias_pct !== null ? `${stats.mean_bias_pct > 0 ? '+' : ''}${stats.mean_bias_pct}%` : '—'} />
            <StatBox label="Median bias" value={stats.median_bias_pct !== null ? `${stats.median_bias_pct > 0 ? '+' : ''}${stats.median_bias_pct}%` : '—'} />
            <StatBox label="MAE" value={stats.mean_abs_error_pct !== null ? `${stats.mean_abs_error_pct}%` : '—'} />
            <StatBox label="Correction ×" value={stats.suggested_correction_factor ?? '—'} />
          </div>
        )}
        {stats?.note && (
          <p className="text-xs text-muted-foreground italic flex items-start gap-2" data-testid="calibration-note">
            <ChartLine size={14} className="shrink-0 mt-0.5" />
            {stats.note}
          </p>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 rounded-lg border border-border/60 bg-muted/20" data-testid="calibration-form">
          <div className="md:col-span-2">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Address / property</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="e.g., 3333 Balboa Way"
              className="mt-1"
              data-testid="calibration-address"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Moasure sqft *</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={form.moasure_sqft}
              onChange={(e) => setForm({ ...form, moasure_sqft: e.target.value })}
              placeholder="4404"
              className="mt-1"
              data-testid="calibration-moasure"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">AI Scan sqft</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={form.ai_sqft}
              onChange={(e) => setForm({ ...form, ai_sqft: e.target.value })}
              placeholder="5100 (optional)"
              className="mt-1"
              data-testid="calibration-ai"
            />
          </div>
          <div className="md:col-span-5">
            <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Optional: zone breakdown, site conditions, anomalies…"
              rows={2}
              className="mt-1"
              data-testid="calibration-notes"
            />
          </div>
          <div className="md:col-span-5 flex justify-end">
            <Button type="submit" disabled={saving} data-testid="calibration-submit">
              <Plus weight="bold" size={16} className="mr-1.5" />
              {saving ? 'Saving…' : 'Log calibration'}
            </Button>
          </div>
        </form>

        {/* Table */}
        <div className="rounded-lg border border-border/60 overflow-hidden" data-testid="calibration-table">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_2fr_auto] gap-2 px-3 py-2 bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <div>Address</div>
            <div className="text-right">Moasure</div>
            <div className="text-right">AI Scan</div>
            <div className="text-right">Bias</div>
            <div>Notes</div>
            <div></div>
          </div>
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="calibration-empty">
              No samples yet. Log your first one above.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_2fr_auto] gap-2 px-3 py-2 border-t border-border/40 text-sm items-center"
                data-testid={`calibration-row-${r.id}`}
              >
                <div className="truncate">
                  <div className="font-medium truncate">{r.address}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString()} · {r.created_by_email}
                  </div>
                </div>
                <div className="text-right font-mono">{r.moasure_sqft?.toLocaleString()}</div>
                <div className="text-right font-mono text-muted-foreground">
                  {r.ai_sqft ? r.ai_sqft.toLocaleString() : '—'}
                </div>
                <div className="text-right">{fmtErr(r.error_pct)}</div>
                <div className="text-xs text-muted-foreground truncate">{r.notes || '—'}</div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleDelete(r.id)}
                  data-testid={`calibration-delete-${r.id}`}
                  title="Delete"
                >
                  <Trash size={14} />
                </Button>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const StatBox = ({ label, value }) => (
  <div className="rounded-md border border-border/60 bg-card px-3 py-2">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-lg font-bold font-mono mt-0.5">{value}</div>
  </div>
);

export default CalibrationCard;
